/**
 * Re-Sync Service
 * 
 * Handles scanning, checking, and updating Excel artifacts in PowerPoint presentations.
 * Used by the Re-Sync Modal to detect and apply changes from source Excel files.
 */

import { getPresentationMetadataRegistry, updateSourceFilePathForExcel } from './metadataService';
import type { MetadataEntry } from './metadataTypes';
import { ArtifactManager, type InsertPosition } from './artifactManager';
import { backendClient } from './communication/backendClient';
import GlobalStateManager from '../main/globals/global-state/globalStateManager';
import type { 
    ScanResult, 
    ArtifactScanItem, 
    MissingFileInfo,
    ScanProgress,
    UpdateResult,
    BatchUpdateResult,
    FileInfoResponse,
    RegenerateArtifactResponse,
    RescanOneArtifactResponse,
} from '../types/resync';
import { toPresentationStatus } from '../types/resync';

// ═══════════════════════════════════════════════════════════════════════════
// CONSTANTS
// ═══════════════════════════════════════════════════════════════════════════

// ═══════════════════════════════════════════════════════════════════════════
// RE-SYNC SERVICE CLASS
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Service for re-syncing Excel artifacts in PowerPoint presentations.
 * 
 * Usage:
 * ```typescript
 * const service = ResyncService.getInstance();
 * 
 * // Scan presentation for artifacts
 * const result = await service.scanPresentation((progress) => {
 *     console.log(`Scanning: ${progress.current}/${progress.total}`);
 * });
 * 
 * // Check status of all artifacts
 * const { artifacts, missingFiles } = await service.checkAllArtifactsStatus(
 *     result.artifacts,
 *     (progress) => console.log(`Checking: ${progress.current}/${progress.total}`)
 * );
 * 
 * // Update outdated artifacts
 * const updateResult = await service.updateArtifacts(
 *     artifacts.filter(a => a.status === "outdated" && a.selected)
 * );
 * ```
 */
export class ResyncService {
    private static instance: ResyncService;
    private artifactManager: ArtifactManager;
    
    private constructor() {
        this.artifactManager = ArtifactManager.getInstance();
    }
    
    public static getInstance(): ResyncService {
        if (!ResyncService.instance) {
            ResyncService.instance = new ResyncService();
        }
        return ResyncService.instance;
    }
    
    // ═══════════════════════════════════════════════════════════════════════════
    // SCANNING
    // ═══════════════════════════════════════════════════════════════════════════
    
    /**
     * Scan the presentation for all Carousel-inserted artifacts.
     * 
     * Finds shapes using the unified metadata registry
     * (presentation-level CustomXmlPart + per-shape entries).
     * 
     * @param onProgress - Progress callback
     * @returns ScanResult with all found artifacts
     */
    public async scanPresentation(
        onProgress?: (progress: ScanProgress) => void
    ): Promise<ScanResult> {
        const artifacts: ArtifactScanItem[] = [];
        const missingFiles: MissingFileInfo[] = [];
        let totalShapes = 0;
        let carouselShapes = 0;
        
        onProgress?.({ phase: "scanning_slides", current: 0, total: 0, message: "Loading metadata..." });

        const registry = await getPresentationMetadataRegistry();
        const defaultExportOptions = {
            blackAndWhite: false,
            collapseGroups: false,
            clearComments: false,
        };

        for (const shapeEntry of Object.values(registry.shapeIndex)) {
            for (const sourceContextId of shapeEntry.sourceContextIds) {
                const entry = registry.entriesById[sourceContextId] as MetadataEntry | undefined;
                if (!entry || entry.type !== "artifact") {
                    continue;
                }

                const payload = entry.payload as {
                    artifactId?: string;
                    excelId?: string;
                    artifactType?: "chart" | "range";
                    source?: {
                        originalFilePath: string;
                        cloneFilePath?: string;
                        sheetName: string;
                        sourceRef: string;
                    };
                    exportOptions?: {
                        blackAndWhite: boolean;
                        collapseGroups: boolean;
                        clearComments: boolean;
                    };
                    insertedAt?: string;
                } | undefined;

                const artifactId = payload?.artifactId || sourceContextId;
                const excelId = payload?.excelId;
                const artifactType = payload?.artifactType;
                const source = payload?.source;

                if (!excelId || !artifactType || !source) {
                    continue;
                }

                artifacts.push({
                    shapeId: shapeEntry.shapeId,
                    slideIndex: shapeEntry.slideIndex,
                    page: shapeEntry.slideIndex + 1,
                    artifactId,
                    excelId,
                    artifactType,
                    source,
                    exportOptions: payload?.exportOptions || defaultExportOptions,
                    status: "checking",
                    presentationStatus: "no_change",
                    lastSyncedAt: payload?.insertedAt || entry.createdAt || new Date().toISOString(),
                });
            }
        }

        carouselShapes = artifacts.length;
        totalShapes = artifacts.length;

        onProgress?.({
            phase: "complete",
            current: carouselShapes,
            total: carouselShapes,
            message: `Found ${carouselShapes} Carousel artifacts`,
        });

        console.log(`[ResyncService] Scan complete: ${carouselShapes} Carousel artifacts`);

        return {
            totalShapes,
            carouselShapes,
            artifacts,
            missingFiles,
        };
    }
    
    // ═══════════════════════════════════════════════════════════════════════════
    // STATUS CHECKING
    // ═══════════════════════════════════════════════════════════════════════════
    
    /**
     * Check if a single artifact is outdated.
     * 
     * 1. Checks if source file exists
     * 2. Compares source lastModified to artifact.lastSyncedAt
     * 3. If outdated, captures current shape image and re-exports from source
     * 
     * @param artifact - The artifact to check
     * @returns Updated artifact with status and images
     */
    public async checkArtifactStatus(
        artifact: ArtifactScanItem
    ): Promise<ArtifactScanItem> {
        const updated = { ...artifact };
        
        try {
            // Check source file
            const sourceFile = artifact.source.cloneFilePath || artifact.source.originalFilePath;
            const fileInfo = await this.getFileInfo(sourceFile);
            
            if (!fileInfo.exists) {
                updated.status = "missing_file";
                updated.error = `Source file not found: ${sourceFile}`;
                updated.presentationStatus = toPresentationStatus(updated.status);
                updated.statusReason = updated.error;
                return updated;
            }
            
            updated.sourceModifiedAt = fileInfo.lastModified || undefined;
            updated.newestAt = fileInfo.lastModified || undefined;
            
            // Compare timestamps
            const lastSyncedDate = new Date(artifact.lastSyncedAt);
            const sourceModifiedDate = fileInfo.lastModified ? new Date(fileInfo.lastModified) : null;
            
            if (!sourceModifiedDate || sourceModifiedDate <= lastSyncedDate) {
                updated.status = "up_to_date";
                updated.presentationStatus = toPresentationStatus(updated.status);
                updated.statusReason = undefined;
                return updated;
            }
            
            // File has been modified - artifact is outdated
            updated.status = "outdated";
            updated.presentationStatus = toPresentationStatus(updated.status);
            updated.statusReason = undefined;
            
            // Capture current shape image
            try {
                const currentImage = await this.captureShapeImage(
                    artifact.slideIndex, 
                    artifact.shapeId
                );
                updated.currentImageBase64 = currentImage || undefined;
            } catch (e) {
                console.warn(`[ResyncService] Could not capture current shape image: ${e}`);
            }
            
            // Re-export from source to get updated image
            try {
                const regenerated = await this.regenerateArtifactFromSource(artifact);
                if (regenerated.success && regenerated.image_base64) {
                    updated.updatedImageBase64 = regenerated.image_base64;
                }
            } catch (e) {
                console.warn(`[ResyncService] Could not regenerate artifact: ${e}`);
            }
            
            return updated;
            
        } catch (error) {
            updated.status = "error";
            updated.presentationStatus = toPresentationStatus(updated.status);
            updated.error = error instanceof Error ? error.message : String(error);
            updated.statusReason = updated.error;
            return updated;
        }
    }
    
    /**
     * Check status of all artifacts (batch).
     * 
     * @param artifacts - Artifacts to check
     * @param onProgress - Progress callback
     * @returns Updated artifacts array and missing files info
     */
    public async checkAllArtifactsStatus(
        artifacts: ArtifactScanItem[],
        onProgress?: (progress: ScanProgress) => void
    ): Promise<{ artifacts: ArtifactScanItem[]; missingFiles: MissingFileInfo[] }> {
        const checkedArtifacts: ArtifactScanItem[] = [];
        const missingFilesMap = new Map<string, MissingFileInfo>();
        
        for (let i = 0; i < artifacts.length; i++) {
            onProgress?.({
                phase: "checking_status",
                current: i + 1,
                total: artifacts.length,
                message: `Checking artifact ${i + 1} of ${artifacts.length}...`,
            });
            
            const checked = await this.checkArtifactStatus(artifacts[i]);
            checkedArtifacts.push(checked);
            
            // Track missing files
            if (checked.status === "missing_file") {
                const filePath = checked.source.cloneFilePath || checked.source.originalFilePath;
                
                if (!missingFilesMap.has(filePath)) {
                    missingFilesMap.set(filePath, {
                        originalPath: filePath,
                        excelId: checked.excelId,
                        affectedShapes: [],
                    });
                }
                
                missingFilesMap.get(filePath)!.affectedShapes.push({
                    shapeId: checked.shapeId,
                    slideIndex: checked.slideIndex,
                    artifactId: checked.artifactId,
                    artifactType: checked.artifactType,
                    sourceRef: checked.source.sourceRef,
                });
            }
        }
        
        onProgress?.({
            phase: "complete",
            current: artifacts.length,
            total: artifacts.length,
            message: "Status check complete",
        });
        
        return {
            artifacts: checkedArtifacts,
            missingFiles: Array.from(missingFilesMap.values()),
        };
    }
    
    // ═══════════════════════════════════════════════════════════════════════════
    // IMAGE CAPTURE
    // ═══════════════════════════════════════════════════════════════════════════
    
    /**
     * Capture the current shape as an image.
     * Uses Office.js shape.getImageAsBase64() (PowerPointApi 1.10)
     * 
     * @param slideIndex - 0-based slide index
     * @param shapeId - PowerPoint shape ID
     * @returns Base64 PNG string, or null if capture fails
     * 
     * TODO: Implement using shape.getImageAsBase64({ format: "png" })
     * when PowerPointApi 1.10 is available in the runtime
     */
    public async captureShapeImage(
        slideIndex: number,
        shapeId: string
    ): Promise<string | null> {
        return PowerPoint.run(async (context) => {
            try {
                const slides = context.presentation.slides;
                slides.load("items");
                await context.sync();
                
                if (slideIndex >= slides.items.length) {
                    return null;
                }
                
                const slide = slides.items[slideIndex];
                const shape = slide.shapes.getItem(shapeId);
                shape.load("id");
                await context.sync();
                
                // TODO: Use shape.getImageAsBase64() when available
                // This requires PowerPointApi 1.10
                // For now, we'll try to use it and fall back gracefully
                try {
                    // @ts-ignore - getImageAsBase64 may not be in type definitions
                    if (shape.getImageAsBase64) {
                        // @ts-ignore
                        const imageResult = shape.getImageAsBase64({ format: "png" });
                        await context.sync();
                        return imageResult.value;
                    }
                } catch (e) {
                    console.warn("[ResyncService] getImageAsBase64 not available:", e);
                }
                
                // Fallback: return null - UI will show placeholder
                return null;
                
            } catch (error) {
                console.error("[ResyncService] Error capturing shape image:", error);
                return null;
            }
        });
    }
    
    // ═══════════════════════════════════════════════════════════════════════════
    // UPDATING
    // ═══════════════════════════════════════════════════════════════════════════
    
    /**
     * Update a single artifact with fresh content from source.
     * 
     * 1. Regenerate artifact from Python backend
     * 2. Get current shape position
     * 3. Delete old shape
     * 4. Insert new shape at same position
     * 5. Update metadata
     * 
     * @param artifact - The artifact to update
     * @returns UpdateResult
     */
    public async updateArtifact(artifact: ArtifactScanItem): Promise<UpdateResult> {
        const startTime = Date.now();
        console.log(`[ResyncService] updateArtifact() START for ${artifact.artifactId}`);
        
        try {
            // 1. Regenerate artifact from source
            console.log(`[ResyncService] Step 1: Regenerating artifact from source...`);
            const regenStart = Date.now();
            const regenerated = await this.regenerateArtifactFromSource(artifact);
            console.log(`[ResyncService] Step 1 complete in ${Date.now() - regenStart}ms, success=${regenerated.success}`);
            
            if (!regenerated.success) {
                console.log(`[ResyncService] Regeneration failed: ${regenerated.error}`);
                return {
                    success: false,
                    artifactId: artifact.artifactId,
                    shapeId: artifact.shapeId,
                    error: regenerated.error || "Failed to regenerate artifact",
                };
            }
            
            // 2. Get current shape position
            console.log(`[ResyncService] Step 2: Getting shape position...`);
            const posStart = Date.now();
            const position = await this.getShapePosition(artifact.slideIndex, artifact.shapeId);
            console.log(`[ResyncService] Step 2 complete in ${Date.now() - posStart}ms, position=`, position);
            
            if (!position) {
                console.log(`[ResyncService] Could not get shape position`);
                return {
                    success: false,
                    artifactId: artifact.artifactId,
                    shapeId: artifact.shapeId,
                    error: "Could not get current shape position",
                };
            }
            
            // 3. Delete old shape
            console.log(`[ResyncService] Step 3: Deleting old shape...`);
            const delStart = Date.now();
            await this.deleteShape(artifact.slideIndex, artifact.shapeId);
            console.log(`[ResyncService] Step 3 complete in ${Date.now() - delStart}ms`);
            
            // 4. Store regenerated artifact in globalStateManager for insertion
            console.log(`[ResyncService] Step 4: Storing artifact in globalStateManager...`);
            const globalState = GlobalStateManager.getInstance();
            const tempArtifact = {
                artifactId: artifact.artifactId,
                excelId: artifact.excelId,
                artifactType: artifact.artifactType,
                source: artifact.source,
                imageBase64: regenerated.image_base64 || "",
                imageFormat: "png",
                oleFilePath: regenerated.ole_file_path || "",
                exportOptions: artifact.exportOptions,
                description: `Re-synced ${artifact.artifactType}`,
                createdAt: Date.now() / 1000,
            };
            globalState.addOrUpdateContext(artifact.artifactId, tempArtifact);
            console.log(`[ResyncService] Step 4 complete, oleFilePath=${regenerated.ole_file_path}`);
            
            // 5. Insert new shape at same position
            console.log(`[ResyncService] Step 5: Inserting new shape...`);
            const insertStart = Date.now();
            const insertResult = await this.artifactManager.insertArtifact(
                artifact.artifactId,
                {
                    slideIndex: artifact.slideIndex,
                    position,
                    preferredMethod: "image", // Always use image for re-sync
                }
            );
            console.log(`[ResyncService] Step 5 complete in ${Date.now() - insertStart}ms, success=${insertResult.success}`);
            
            if (!insertResult.success) {
                console.log(`[ResyncService] Insert failed: ${insertResult.error}`);
                return {
                    success: false,
                    artifactId: artifact.artifactId,
                    shapeId: artifact.shapeId,
                    error: insertResult.error || "Failed to insert updated shape",
                };
            }
            
            const totalTime = Date.now() - startTime;
            console.log(`[ResyncService] updateArtifact() SUCCESS for ${artifact.artifactId} in ${totalTime}ms total`);
            
            return {
                success: true,
                artifactId: artifact.artifactId,
                shapeId: artifact.shapeId,
                newShapeId: insertResult.shapeId,
            };
            
        } catch (error) {
            const totalTime = Date.now() - startTime;
            console.error(`[ResyncService] updateArtifact() ERROR after ${totalTime}ms:`, error);
            return {
                success: false,
                artifactId: artifact.artifactId,
                shapeId: artifact.shapeId,
                error: error instanceof Error ? error.message : String(error),
            };
        }
    }
    
    /**
     * Update multiple artifacts.
     * 
     * @param artifacts - Artifacts to update
     * @param onProgress - Progress callback
     * @returns BatchUpdateResult
     */
    public async updateArtifacts(
        artifacts: ArtifactScanItem[],
        onProgress?: (current: number, total: number) => void
    ): Promise<BatchUpdateResult> {
        console.log(`[ResyncService] updateArtifacts() START - ${artifacts.length} artifacts`);
        const batchStartTime = Date.now();
        const results: UpdateResult[] = [];
        let succeeded = 0;
        let failed = 0;
        
        for (let i = 0; i < artifacts.length; i++) {
            console.log(`[ResyncService] Processing artifact ${i + 1}/${artifacts.length}: ${artifacts[i].artifactId}`);
            onProgress?.(i + 1, artifacts.length);
            
            const result = await this.updateArtifact(artifacts[i]);
            console.log(`[ResyncService] Artifact ${i + 1} result: success=${result.success}, error=${result.error || 'none'}`);
            results.push(result);
            
            if (result.success) {
                succeeded++;
            } else {
                failed++;
            }
        }
        
        const batchDuration = Date.now() - batchStartTime;
        console.log(`[ResyncService] updateArtifacts() COMPLETE in ${batchDuration}ms - ${succeeded} succeeded, ${failed} failed`);
        
        return {
            total: artifacts.length,
            succeeded,
            failed,
            results,
        };
    }
    
    // ═══════════════════════════════════════════════════════════════════════════
    // FILE RELOCATION
    // ═══════════════════════════════════════════════════════════════════════════
    
    /**
     * Update the stored path for a source file that was moved.
     * 
     * @param excelId - The Excel source ID
     * @param newPath - The new file path
     */
    public async relocateSourceFile(excelId: string, newPath: string): Promise<void> {
        await updateSourceFilePathForExcel(excelId, newPath);
        console.log(`[ResyncService] Relocated source file ${excelId} to ${newPath}`);
    }

    /**
     * Re-check a single artifact from its source and map to UI-friendly statuses.
     * Used by single-item "Scan Again" in review mode.
     */
    public async rescanOneArtifact(
        artifact: ArtifactScanItem,
        forceRegenerate: boolean = true
    ): Promise<ArtifactScanItem> {
        const sourceFile = artifact.source.cloneFilePath || artifact.source.originalFilePath;
        const requestBody = {
            source_file_path: sourceFile,
            sheet_name: artifact.source.sheetName,
            artifact_type: artifact.artifactType,
            source_ref: artifact.source.sourceRef,
            last_synced_at: artifact.lastSyncedAt,
            export_options: {
                black_and_white: artifact.exportOptions?.blackAndWhite ?? false,
                collapse_groups: artifact.exportOptions?.collapseGroups ?? false,
                clear_comments: artifact.exportOptions?.clearComments ?? false,
            },
            force_regenerate: forceRegenerate,
        };

        const data = await backendClient.rescanOneExcelArtifact(requestBody) as RescanOneArtifactResponse;

        const updated: ArtifactScanItem = { ...artifact };
        updated.sourceModifiedAt = data.source_last_modified || undefined;
        updated.newestAt = data.source_last_modified || undefined;
        updated.updatedImageBase64 = data.updated_image_base64 || undefined;

        switch (data.status) {
            case "new":
                updated.status = "outdated";
                updated.presentationStatus = "new";
                updated.statusReason = undefined;
                updated.error = undefined;
                break;
            case "no_change":
                updated.status = "up_to_date";
                updated.presentationStatus = "no_change";
                updated.statusReason = undefined;
                updated.error = undefined;
                break;
            case "not_found":
                updated.status = "missing_file";
                updated.presentationStatus = "not_found";
                updated.error = data.error || "Source content not found";
                updated.statusReason = updated.error;
                break;
            case "error":
            default:
                updated.status = "error";
                updated.presentationStatus = "error";
                updated.error = data.error || "Rescan failed";
                updated.statusReason = updated.error;
                break;
        }

        return updated;
    }
    
    // ═══════════════════════════════════════════════════════════════════════════
    // PRIVATE HELPERS
    // ═══════════════════════════════════════════════════════════════════════════
    
    /**
     * Get file info from Python backend.
     */
    private async getFileInfo(filePath: string): Promise<FileInfoResponse> {
        try {
            const data = await backendClient.getExcelFileModifiedInfo(filePath);
            return {
                exists: data.exists,
                lastModified: data.last_modified,
                sizeBytes: data.size_bytes,
                error: data.error || null,
            };
        } catch (error) {
            return {
                exists: false,
                lastModified: null,
                sizeBytes: null,
                error: error instanceof Error ? error.message : String(error),
            };
        }
    }
    
    /**
     * Regenerate artifact from source via Python backend.
     */
    private async regenerateArtifactFromSource(
        artifact: ArtifactScanItem
    ): Promise<RegenerateArtifactResponse> {
        try {
            const sourceFile = artifact.source.cloneFilePath || artifact.source.originalFilePath;
            return await backendClient.regenerateExcelArtifact({
                source_file_path: sourceFile,
                sheet_name: artifact.source.sheetName,
                artifact_type: artifact.artifactType,
                source_ref: artifact.source.sourceRef,
                export_options: {
                    black_and_white: artifact.exportOptions?.blackAndWhite ?? false,
                    collapse_groups: artifact.exportOptions?.collapseGroups ?? false,
                    clear_comments: artifact.exportOptions?.clearComments ?? false,
                },
            }) as RegenerateArtifactResponse;
        } catch (error) {
            return {
                success: false,
                error: error instanceof Error ? error.message : String(error),
            };
        }
    }
    
    /**
     * Get the position of a shape.
     */
    private async getShapePosition(
        slideIndex: number,
        shapeId: string
    ): Promise<InsertPosition | null> {
        return PowerPoint.run(async (context) => {
            try {
                const slides = context.presentation.slides;
                slides.load("items");
                await context.sync();
                
                if (slideIndex >= slides.items.length) {
                    return null;
                }
                
                const slide = slides.items[slideIndex];
                const shape = slide.shapes.getItem(shapeId);
                shape.load("left,top,width,height");
                await context.sync();
                
                return {
                    left: shape.left,
                    top: shape.top,
                    width: shape.width,
                    height: shape.height,
                };
            } catch (error) {
                console.error("[ResyncService] Error getting shape position:", error);
                return null;
            }
        });
    }
    
    /**
     * Delete a shape from a slide.
     */
    private async deleteShape(slideIndex: number, shapeId: string): Promise<void> {
        return PowerPoint.run(async (context) => {
            const slides = context.presentation.slides;
            slides.load("items");
            await context.sync();
            
            if (slideIndex >= slides.items.length) {
                throw new Error(`Slide index ${slideIndex} out of range`);
            }
            
            const slide = slides.items[slideIndex];
            const shape = slide.shapes.getItem(shapeId);
            shape.delete();
            await context.sync();
            
            console.log(`[ResyncService] Deleted shape ${shapeId} from slide ${slideIndex}`);
        });
    }
}

// ═══════════════════════════════════════════════════════════════════════════
// SINGLETON ACCESSOR
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Get the ResyncService singleton instance.
 */
export function getResyncService(): ResyncService {
    return ResyncService.getInstance();
}

export default ResyncService;





